import React from 'react';
import { Box, Container, Typography, Chip, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useT } from '../i18n/useT';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';

const ResellersSection: React.FC = () => {
  const { r } = useT();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const partners = r.resellers.partners;
  const become = r.resellers.becomePartner;

  const singlePartner = partners.length === 1;

  return (
    <Box
      component="section"
      id="partners"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: 'white',
        borderTop: '1px solid #E2E8F0',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              icon={<HandshakeRoundedIcon sx={{ color: '#0F172A !important' }} />}
              label={r.resellers.eyebrow}
              sx={{
                mb: 3,
                px: 1.2,
                py: 2.5,
                backgroundColor: '#F8FAFC',
                color: '#0F172A',
                fontWeight: 700,
                fontSize: '0.78rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                border: '1px solid #E2E8F0',
              }}
            />
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.6rem' },
                color: '#0F172A',
                letterSpacing: '-0.02em',
              }}
            >
              {r.resellers.title}
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: '#475569', maxWidth: '850px', mx: 'auto', lineHeight: 1.6, fontWeight: 400 }}
            >
              {r.resellers.subtitle}
            </Typography>
          </Box>
        </motion.div>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: singlePartner
              ? { xs: '1fr' }
              : { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 3,
            justifyContent: 'center',
            maxWidth: singlePartner ? 720 : '100%',
            mx: 'auto',
            mb: 6,
          }}
        >
          {partners.map((partner, idx) => (
            <motion.div
              key={partner.name}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.15 * idx }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  backgroundColor: 'white',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                  transition: 'all 0.25s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: '#CBD5E1',
                    boxShadow: '0 16px 44px rgba(15, 23, 42, 0.08)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    backgroundColor: '#0F172A',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip
                    icon={<PlaceRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                    label={partner.region}
                    size="small"
                    sx={{
                      background: '#F1F5F9',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      border: '1px solid #E2E8F0',
                    }}
                  />
                  <Chip
                    icon={<VerifiedRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                    label="Official Partner"
                    size="small"
                    sx={{
                      backgroundColor: '#0F172A',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      letterSpacing: '0.04em',
                      '& .MuiChip-icon': { color: 'white' },
                    }}
                  />
                </Box>

                <Typography
                  component="div"
                  sx={{
                    fontSize: { xs: '2.4rem', md: '3rem' },
                    fontWeight: 900,
                    lineHeight: 1,
                    mb: 0.5,
                    letterSpacing: '-0.02em',
                    background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {partner.wordmark}
                </Typography>
                <Typography
                  sx={{
                    color: '#475569',
                    fontSize: '0.95rem',
                    fontStyle: 'italic',
                    mb: 2.5,
                  }}
                >
                  {partner.tagline}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: '#475569',
                    lineHeight: 1.65,
                    fontSize: '0.97rem',
                    mb: 3,
                  }}
                >
                  {partner.description}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 3 }}>
                  {partner.badges.map((badge) => (
                    <Chip
                      key={badge}
                      label={badge}
                      size="small"
                      sx={{
                        background: '#F8FAFC',
                        color: '#1E293B',
                        fontWeight: 500,
                        fontSize: '0.78rem',
                        border: '1px solid #E2E8F0',
                      }}
                    />
                  ))}
                </Box>

                <Box sx={{ mt: 'auto' }}>
                  <Button
                    component="a"
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${partner.cta} (abre en una nueva ventana)`}
                    endIcon={<OpenInNewRoundedIcon />}
                    fullWidth
                    sx={{
                      py: 1.3,
                      borderRadius: '50px',
                      backgroundColor: '#0F172A',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      textTransform: 'none',
                      boxShadow: '0 6px 20px rgba(15, 23, 42, 0.20)',
                      '&:hover': {
                        backgroundColor: '#1E293B',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.28)',
                      },
                    }}
                  >
                    {partner.cta}
                  </Button>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Box
            sx={{
              maxWidth: 760,
              mx: 'auto',
              p: { xs: 2.5, md: 3 },
              borderRadius: 2,
              backgroundColor: '#F8FAFC',
              border: '1px dashed #CBD5E1',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#0F172A', fontSize: '1rem', mb: 0.3 }}>
                {become.title}
              </Typography>
              <Typography sx={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.5 }}>
                {become.description}
              </Typography>
            </Box>
            <Button
              component="a"
              href={`mailto:${become.email}?subject=Programa%20de%20partners%20WAI`}
              startIcon={<EmailRoundedIcon />}
              variant="outlined"
              sx={{
                flexShrink: 0,
                borderRadius: '50px',
                borderColor: '#0F172A',
                color: '#0F172A',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                py: 1,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: '#0F172A',
                  backgroundColor: 'rgba(15, 23, 42, 0.04)',
                },
              }}
            >
              {become.cta}
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ResellersSection;
